/**
 * Biometric Authentication (WebAuthn/FIDO2)
 * Allows users to login with fingerprint/face on supported devices.
 * Falls back gracefully on unsupported browsers.
 */

const CREDENTIAL_KEY = 'majelani-biometric-credential';

/**
 * Check if WebAuthn is supported on this device
 */
export function isBiometricSupported(): boolean {
  return !!(
    window.PublicKeyCredential &&
    navigator.credentials &&
    typeof navigator.credentials.create === 'function'
  );
}

/**
 * Check if user has enrolled biometric for this device
 */
export function isBiometricEnrolled(): boolean {
  return !!localStorage.getItem(CREDENTIAL_KEY);
}

/**
 * Enroll biometric credential for the current user
 */
export async function enrollBiometric(userId: string, userName: string): Promise<boolean> {
  if (!isBiometricSupported()) return false;

  try {
    const challenge = crypto.getRandomValues(new Uint8Array(32));
    
    const credential = await navigator.credentials.create({
      publicKey: {
        challenge,
        rp: {
          name: 'Majelani Accounting',
        },
        user: {
          id: new TextEncoder().encode(userId),
          name: userName,
          displayName: userName,
        },
        pubKeyCredParams: [
          { alg: -7, type: 'public-key' },   // ES256
          { alg: -257, type: 'public-key' },  // RS256
        ],
        authenticatorSelection: {
          authenticatorAttachment: 'platform', // Built-in (fingerprint/face)
          userVerification: 'preferred',
          residentKey: 'preferred',
        },
        timeout: 120000,
      },
    }) as PublicKeyCredential | null;

    if (credential) {
      // Store credential ID for future authentication
      const credentialId = btoa(String.fromCharCode(...new Uint8Array(credential.rawId)));
      localStorage.setItem(CREDENTIAL_KEY, JSON.stringify({
        credentialId,
        userId,
        userName,
        enrolledAt: Date.now(),
      }));
      return true;
    }
    return false;
  } catch (err) {
    // Silent fail — logged only in development
    if (process.env.NODE_ENV === 'development') {
      console.debug('[Biometric] Enrollment not completed:', (err as any)?.name);
    }
    return false;
  }
}

/**
 * Authenticate with biometric (fingerprint/face)
 * Returns the stored userId if successful, null if failed
 */
export async function authenticateWithBiometric(): Promise<{ userId: string; userName: string } | null> {
  if (!isBiometricSupported() || !isBiometricEnrolled()) return null;

  try {
    const stored = JSON.parse(localStorage.getItem(CREDENTIAL_KEY) || '{}');
    const credentialId = Uint8Array.from(atob(stored.credentialId), c => c.charCodeAt(0));
    const challenge = crypto.getRandomValues(new Uint8Array(32));

    const assertion = await navigator.credentials.get({
      publicKey: {
        challenge,
        allowCredentials: [{
          id: credentialId,
          type: 'public-key',
          transports: ['internal'],
        }],
        userVerification: 'preferred',
        timeout: 120000,
      },
    }) as PublicKeyCredential | null;

    if (assertion) {
      return { userId: stored.userId, userName: stored.userName };
    }
    return null;
  } catch (err) {
    if (process.env.NODE_ENV === 'development') {
      console.debug('[Biometric] Auth not completed:', (err as any)?.name);
    }
    return null;
  }
}

/**
 * Remove biometric enrollment
 */
export function removeBiometric(): void {
  localStorage.removeItem(CREDENTIAL_KEY);
}
